import { useMemo } from "react";
import { ScreenHeader } from "../components/ScreenHeader";
import { Link } from "react-router-dom";
import { useManagerTeam, useTeamTimesheets } from "../hooks/useManagerTeam";
import { useTeamCorrectionRequests } from "../hooks/useCorrectionRequests";
import { useTeamTimelineDays } from "../hooks/useTeamTimelineDays";
import { useTeamTimeDays } from "../hooks/useTeamTimeDays";
import { isSettled, teamBucket } from "../lib/anomalyState";
import { localDayKey, localMidnight } from "../lib/date";
import { groupByLocalDay, latestRecordedAt, recapDay } from "../lib/dayRecap";
import {
  formatClock,
  formatDateFull,
  formatDuration,
  formatPeriodRange,
  plural,
  receivedDayPrefix,
} from "../lib/format";
import { isOverdue, sortReviewQueue } from "../lib/review";
import { sortByWorkerLabel, workerBadge, workerLabel } from "../lib/team";
import { minToLabel } from "../lib/timelineSlots";

// Jours ouvrés, lundi à vendredi (getDay : 0 = dimanche). TAWA n'expose pas
// `work_days` du tenant, et le rythme de chacun (astreinte, week-end travaillé)
// relève du futur module Workforce Planning. Un jour non ouvré ne déclenche
// donc aucune alerte « sans pointage », mais ceux qui ont pointé restent
// affichés.
const WORK_DAYS = new Set([1, 2, 3, 4, 5]);
const MAX_NAMES = 5;

function listNames(names: string[]): string {
  if (names.length <= MAX_NAMES) return names.join(", ");
  const rest = names.length - MAX_NAMES;
  return `${names.slice(0, MAX_NAMES).join(", ")} et ${rest} autre${plural(rest)}`;
}

function PointingLine({
  workDay,
  settled,
  unreadable,
  silentNames,
  pointedCount,
}: {
  workDay: boolean;
  settled: number;
  unreadable: number;
  silentNames: string[];
  pointedCount: number;
}) {
  // Rien d'affirmé tant qu'aucun membre n'a pu être lu.
  if (settled === 0) return null;
  const scope = unreadable > 0 ? `Parmi les ${settled} lus, ` : "";
  if (!workDay) {
    return (
      <div className="recap-line">
        {pointedCount === 0
          ? "Jour non ouvré : personne n'a pointé."
          : `Jour non ouvré : ${pointedCount} collaborateur${plural(pointedCount)} ${pointedCount > 1 ? "ont" : "a"} pointé.`}
      </div>
    );
  }
  if (silentNames.length === 0) {
    return (
      <div className="recap-line">
        {scope ? `${scope}tous ont pointé hier` : "Tout le monde a pointé hier"}
      </div>
    );
  }
  return (
    <div className="recap-line warn">
      {scope}
      {silentNames.length} collaborateur{plural(silentNames.length)} sans
      pointage hier : {listNames(silentNames)}
    </div>
  );
}

export function DigestScreen() {
  const now = new Date();
  const since = localMidnight(now, -1);
  const yesterday = localDayKey(since);
  const workDay = WORK_DAYS.has(since.getDay());

  const team = useManagerTeam();
  const workers = team.data?.workers ?? [];
  const { byWorker, isLoading: timelinesLoading } = useTeamTimelineDays(
    workers.map((w) => w.worker_url),
    [yesterday, localDayKey(now)],
  );
  const teamTimeDays = useTeamTimeDays(
    workers.map((w) => w.worker_url),
    yesterday,
    yesterday,
  );
  const anomalies = useTeamCorrectionRequests();
  const sheets = useTeamTimesheets();

  const allAnomalies = anomalies.data?.requests ?? [];
  const fresh = allAnomalies.filter(
    (a) => !isSettled(a) && Date.parse(a.detected_at) >= since.getTime(),
  );
  const toHandle = fresh.filter(
    (a) => teamBucket(a, allAnomalies) === "to_handle",
  ).length;
  const atWorker = fresh.filter(
    (a) => teamBucket(a, allAnomalies) === "at_worker",
  ).length;

  const queue = sortReviewQueue(sheets.data?.timesheets ?? []);
  const oldest = queue[0];
  const overdue = queue.filter((ts) => isOverdue(ts, now)).length;

  const rows = useMemo(
    () =>
      sortByWorkerLabel(workers).map((w) => {
        const timeline = byWorker.get(w.worker_url);
        const dayEvents =
          groupByLocalDay(timeline?.events ?? []).get(yesterday) ?? [];
        return {
          worker: w,
          timeline,
          recap: recapDay(
            yesterday,
            dayEvents,
            teamTimeDays.get(w.worker_url)?.get(yesterday),
          ),
          newAnomalies: fresh.filter((a) => a.worker_url === w.worker_url)
            .length,
        };
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [workers, byWorker, teamTimeDays, yesterday, anomalies.dataUpdatedAt],
  );
  const settled = rows.filter(
    (r) => r.timeline && !r.timeline.isLoading && !r.timeline.isError,
  );
  const silentNames = settled
    .filter((r) => !r.recap.hasEvents)
    .map((r) => workerLabel(r.worker));
  const pointedCount = settled.filter((r) => r.recap.hasEvents).length;
  const unreadable = rows.filter((r) => r.timeline?.isError).length;

  // Fraîcheur de l'équipe seule : les pointages du manager, s'il badge, ne
  // doivent pas masquer une remontée d'équipe en panne.
  const receivedAt = latestRecordedAt(
    [...byWorker.values()]
      .flatMap((t) => t.events)
      .filter((ev) => Date.parse(ev.occurred_at) >= since.getTime()),
  );
  const freshnessLoading = team.isLoading || timelinesLoading;

  return (
    <div className="team-page">
      <ScreenHeader title="Récap de la veille" sub={formatDateFull(since)} />

      <div className="recap-grid">
        <section className="dcard" aria-label="Fraîcheur des données">
          <div className="dcard-h">
            <span className="ttl">Données</span>
          </div>
          <div className="dcard-body">
            <div className="recap-big">
              {freshnessLoading
                ? "Chargement…"
                : receivedAt
                  ? `Données reçues ${receivedDayPrefix(receivedAt)}à ${formatClock(receivedAt)}`
                  : "Aucune donnée reçue depuis hier"}
            </div>
            {team.isSuccess && (
              <PointingLine
                workDay={workDay}
                settled={settled.length}
                unreadable={unreadable}
                silentNames={silentNames}
                pointedCount={pointedCount}
              />
            )}
            {unreadable > 0 && (
              <div className="recap-line">
                {unreadable} collaborateur{plural(unreadable)} illisible
                {plural(unreadable)} : leurs pointages n'ont pas pu être lus.
              </div>
            )}
          </div>
        </section>

        <section className="dcard" aria-label="Anomalies">
          <div className="dcard-h">
            <span className="ttl">Anomalies</span>
          </div>
          <div className="dcard-body">
            {anomalies.isLoading && (
              <div className="recap-line">Chargement…</div>
            )}
            {anomalies.isError && (
              <div className="recap-line">Anomalies indisponibles.</div>
            )}
            {anomalies.isSuccess && (
              <>
                <div className="recap-big">
                  {fresh.length} nouvelle{plural(fresh.length)} depuis hier
                </div>
                <div className={`recap-line${toHandle ? " warn" : ""}`}>
                  À traiter : {toHandle}
                </div>
                <div className="recap-line">
                  Chez le collaborateur : {atWorker}
                </div>
              </>
            )}
            <Link className="recap-link" to="/anomalies">
              Voir les anomalies
            </Link>
          </div>
        </section>

        <section className="dcard" aria-label="Feuilles à valider">
          <div className="dcard-h">
            <span className="ttl">Feuilles à valider</span>
          </div>
          <div className="dcard-body">
            {sheets.isLoading && <div className="recap-line">Chargement…</div>}
            {sheets.isError && (
              <div className="recap-line">Feuilles indisponibles.</div>
            )}
            {sheets.isSuccess && (
              <>
                <div className="recap-big">
                  {queue.length} feuille{plural(queue.length)} en attente
                </div>
                {oldest && (
                  <div className="recap-line">
                    Plus ancienne :{" "}
                    {formatPeriodRange(oldest.period_start, oldest.period_end)}
                  </div>
                )}
                <div className={`recap-line${overdue ? " warn" : ""}`}>
                  En retard : {overdue}
                </div>
              </>
            )}
            <Link className="recap-link" to="/revue">
              Voir les feuilles
            </Link>
          </div>
        </section>
      </div>

      <h2 className="recap-h">Équipe hier</h2>
      {team.isLoading && (
        <>
          {[1, 2, 3].map((i) => (
            <div key={i} className="skel-row" />
          ))}
        </>
      )}
      {team.isError && <div className="empty-state">Équipe indisponible.</div>}
      {team.isSuccess && workers.length === 0 && (
        <div className="empty-state">Aucun collaborateur rattaché.</div>
      )}
      {rows.length > 0 && (
        <>
          <div className="team-rows">
            {/* Chaque cellule porte son libellé (.time-k), comme l'onglet Temps
                équipe : l'en-tête disparaît sous 560px. */}
            <div className="team-row time-row time-head" aria-hidden>
              <span className="row-title">Collaborateur</span>
              <span className="time-cell">Heures</span>
              <span className="time-cell wide">Amplitude</span>
              <span className="time-cell">Anomalies</span>
            </div>
            {rows.map(({ worker, timeline, recap, newAnomalies }) => {
              const badge = workerBadge(worker);
              const state =
                !timeline || timeline.isLoading
                  ? "…"
                  : timeline.isError
                    ? "Indisponible"
                    : null;
              return (
                <div key={worker.worker_url} className="team-row time-row">
                  <span
                    className="row-avatar"
                    style={{ background: badge.color }}
                    aria-hidden
                  >
                    {badge.initials}
                  </span>
                  <span className="row-title">{badge.label}</span>
                  <span className="time-cell mono">
                    {state ??
                      (recap.hasEvents
                        ? formatDuration(recap.workedMin * 60000).short
                        : "-")}
                    <span className="time-k"> hier</span>
                  </span>
                  <span className="time-cell wide mono">
                    <span className="time-k">Amplitude </span>
                    {state ??
                      (recap.firstMin !== null && recap.lastMin !== null
                        ? `${minToLabel(recap.firstMin)} → ${minToLabel(recap.lastMin)}`
                        : "Aucun pointage")}
                  </span>
                  <span
                    className={`time-cell mono${newAnomalies ? " warn" : ""}`}
                  >
                    {newAnomalies}
                    <span className="time-k">
                      {" "}
                      anomalie{plural(newAnomalies)}
                    </span>
                  </span>
                </div>
              );
            })}
          </div>
          <Link className="recap-link" to="/equipe?tab=temps">
            Voir le temps de l'équipe
          </Link>
        </>
      )}
    </div>
  );
}

export default DigestScreen;
